import React, { memo, useCallback, useMemo, useState } from "react";
import { Slider } from "../../../../components/Slider/Slider.jsx";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import { Loader } from "../../../../components/Loader/Loader.jsx";
import { closeLiteTradePositionAction } from "../../../../modules/LiteTrade/liteTradeActions.js";
import { Button } from "react-bootstrap";
import {
    getLiteTradeAssets,
    makeSelectLiteTradeMarketDataBySymbol,
    makeSelectorGetLiteTradeAccountMetrics,
    makeSelectorGetLiteTradeAccountPositionInfo
} from "../../../../modules/LiteTrade/liteTradeSelectors.js";
import { LITE_TRADE_POSITION_SIDE } from "../../../../modules/LiteTrade/liteTradeConstants.js";
import { BinIcon, IconAsset } from "../../../../components/icon/Icon.jsx";
import _ from "lodash";
import { getUserSelectedAccountCode } from "../../../../app/appSelectors.js";
import amountFormat from "../../../../utils/amountFormat.js";
import classNames from "classnames";
import {
    SlideMobileModalBoxStyled,
    SlideMobileModalHeaderStyled,
    SlideMobileModalOrderProtectionStyled
} from "./styled/SlideMobileStyled.js";
import { MOBILE_SLIDER_PAGES_IDS } from "../mobileSliderPagesConstants.js";
import { openMobileSliderPageAction } from "../../../mobileAppActions.js";
import {
    makeSelectorMobilePositionUpdateProtectionOrderInfo
} from "../../MobilePosition/MobilePositionUpdate/mobilePositionUpdateSelectors.js";
import { ORDER_TYPES } from "../../../../components/OrderTypeSelector/orderTypeSelectorConstants.js";
import LiteTradePortfolioPositionInfo
    from "../../../../modules/LiteTrade/components/LiteTradePortfolio/LiteTradePortfolioPositionInfo.jsx";
import { MobileAssetInfoSellBuyUnavailableStyled } from "../../MobileAssetInfo/styled/MobileAssetInfoOverviewStyled.js";
import { useLangContext } from "../../../../providers/LangProvider/LangProviderContext";

function SlideMobilePositionModal(props) {
    const {
        assets,
        assetInstance,
        closePosition,
        openAssetOnClick,
        marketDataSymbol,
        metrics,
        onClose,
        openMobileSliderPage,
        positionCode, // used at selector
        positionInfo,
        selectedAccountCode,
        orderStopLoss,
        orderTakeProfit,
    } = props;
    const { getLangPath } = useLangContext();
    const [closingPosition, setClosingPosition] = useState(false);

    const closePositionHandler = useCallback((item) => {
        setClosingPosition(true);
        closePosition({
            positionCode: positionInfo.positionCode,
            quantity: positionInfo.quantity,
            side: positionInfo.side === LITE_TRADE_POSITION_SIDE.SELL ? LITE_TRADE_POSITION_SIDE.BUY : LITE_TRADE_POSITION_SIDE.SELL,
            symbol: positionInfo.symbol,
            onSuccessCallback: onClose,
        });
    }, [closePosition, onClose, positionInfo]);

    const editPositionHandler = useCallback(() => {
        openMobileSliderPage({
            name: MOBILE_SLIDER_PAGES_IDS.POSITION_UPDATE,
            'position-code': positionInfo.positionCode,
        });
        onClose();
    }, [onClose, openMobileSliderPage, positionInfo]);

    const asset = useMemo(() => _.find(assets, { symbol: positionInfo.symbol }) || {}, [assets, positionInfo.symbol]);

    const getCurrentPrice = useCallback((forPl = false) => {
        let side = positionInfo.side === LITE_TRADE_POSITION_SIDE.BUY ? 'ask' : 'bid';
        if (forPl) side = side === 'ask' ? 'bid' : 'ask';
        return marketDataSymbol?.[side] ?? 0;
    }, [marketDataSymbol, positionInfo.side]);

    const clickAssetHandler = useCallback((e) => {
        e.preventDefault();
        if (openAssetOnClick) {
            openMobileSliderPage({
                name: MOBILE_SLIDER_PAGES_IDS.ASSET,
                symbol: positionInfo.symbol,
            });
        }
    }, [openAssetOnClick, openMobileSliderPage, positionInfo.symbol]);

    const pl = useMemo(() => assetInstance.getPositionPL(positionInfo, getCurrentPrice(true)), [assetInstance, getCurrentPrice, positionInfo]);

    if (!positionInfo) {
        return <Loader show={true}/>
    }

    return <Slider showCloseButton={false}
                   placement={'bottom'}
                   show={true}
                   onClose={onClose}
    >
        {
            !!closingPosition && <Loader show={true}/>
        }
        <div className="d-flex flex-column">
            <SlideMobileModalHeaderStyled>
                <div className="close" onClick={onClose}>
                    <BinIcon name={'IcClose'} size={24} slyle={{
                        fill: 'rgba(255, 255, 255, 0.6)'
                    }}/>
                </div>
            </SlideMobileModalHeaderStyled>
            <SlideMobileModalBoxStyled>
                <div className="d-flex gap-3" onClick={clickAssetHandler}>
                    <div className="f28">
                        <IconAsset symbol={positionInfo.symbol}/>
                    </div>
                    <div className="d-flex flex-column">
                        <div className="f16">{asset.info.description}</div>
                        <div className={'fw600 text-secondary'}>
                            <LiteTradePortfolioPositionInfo
                                item={{
                                    ...positionInfo,
                                    assetInstance,
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="d-flex justify-content-between mt-4 align-items-center mb-4">
                    <div className="f20 fw600">{assetInstance.amountToFixed(getCurrentPrice())}</div>
                    <div className={classNames("d-flex f16 fw600", {
                        'negative': pl < 0,
                        'positive': pl >= 0,
                    })}>{pl !== null && amountFormat(pl, 2, '$')}
                    </div>
                </div>
                {
                    !!orderStopLoss && <SlideMobileModalOrderProtectionStyled>
                        <div className="label">Stop loss</div>
                        <div className="price">{assetInstance.amountToFixed(orderStopLoss.legs[0].price)}</div>
                    </SlideMobileModalOrderProtectionStyled>
                }
                {
                    !!orderTakeProfit && <SlideMobileModalOrderProtectionStyled>
                        <div className="label">Take profit</div>
                        <div className="price">{assetInstance.amountToFixed(orderTakeProfit.legs[0].price)}</div>
                    </SlideMobileModalOrderProtectionStyled>
                }
            </SlideMobileModalBoxStyled>
            {
                !!assetInstance.isTradingNow() ? <>
                    <div className="d-flex flex-column mt-3">
                        <Button size="lg" variant="info" onClick={editPositionHandler}>
                            {getLangPath("lite_version.portfolio.edit_position")}
                        </Button>
                    </div>
                    <div className="d-flex justify-content-center mt-3">
                        <Button size="lg" variant="outline-info negative" onClick={closePositionHandler}>
                            {getLangPath("lite_version.portfolio.close_position")}
                        </Button>
                    </div>
                </> : <>
                    <MobileAssetInfoSellBuyUnavailableStyled>
                        {getLangPath('lite_version.portfolio.inactive_by_schedule')}
                    </MobileAssetInfoSellBuyUnavailableStyled>
                </>
            }
        </div>
    </Slider>;
}

const mapStateToProps = createStructuredSelector({
    assets: getLiteTradeAssets,
    marketDataSymbol: (state, props) => makeSelectLiteTradeMarketDataBySymbol(props.symbol)(state, props),
    metrics: (state, props) => makeSelectorGetLiteTradeAccountMetrics(props.accountCode)(state, props),
    positionInfo: (state, props) => makeSelectorGetLiteTradeAccountPositionInfo(props.positionCode)(state, props),
    orderStopLoss: (state, props) => makeSelectorMobilePositionUpdateProtectionOrderInfo({
        orderId: props.orderId,
        positionCode: props.positionCode,
        protectionOrderType: ORDER_TYPES.STOP,
    })(state, props),
    orderTakeProfit: (state, props) => makeSelectorMobilePositionUpdateProtectionOrderInfo({
        orderId: props.orderId,
        positionCode: props.positionCode,
        protectionOrderType: ORDER_TYPES.LIMIT,
    })(state, props),
    selectedAccountCode: getUserSelectedAccountCode,
});

const mapDispatchToProps = (dispatch) => ({
    closePosition: payload => dispatch(closeLiteTradePositionAction(payload)),
    openMobileSliderPage: payload => dispatch(openMobileSliderPageAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(SlideMobilePositionModal));
